import { useMemo } from 'react';
import { format } from 'date-fns';
import { Formik } from 'formik';
import * as yup from 'yup';
import {
  useTheme,
  useMediaQuery,
  Box,
  Typography,
  Button,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
} from '@mui/material';
import { WbSunny, AcUnit, CalendarMonth } from '@mui/icons-material';


const STATUS_OPTIONS = ['draft', 'active', 'published', 'closed'];

const STATUS_DOT = {
  draft: '#9e9e9e',
  active: '#1976d2',
  published: '#2e7d32',
  closed: '#d32f2f',
};

const schema = yup.object({
  seasonCode: yup.string().required('Required'),
  seasonName: yup.string().required('Required'),
  iataCode: yup.string().oneOf(['S', 'W']).required(),
  year: yup.number().min(2020).max(2050).required('Required'),
  startDate: yup.string().required('Required'),
  endDate: yup.string().required('Required'),
  status: yup.string().oneOf(STATUS_OPTIONS),
  baseAirline: yup.string(),
  notes: yup.string(),
});

const SeasonFormDialog = ({ open, onClose, data, onSave }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const initialValues = useMemo(() => data ? {
    seasonCode: data.seasonCode || '',
    seasonName: data.seasonName || '',
    iataCode: data.iataCode || 'S',
    year: data.year || new Date().getFullYear(),
    startDate: data.startDate ? format(new Date(data.startDate), 'yyyy-MM-dd') : '',
    endDate: data.endDate ? format(new Date(data.endDate), 'yyyy-MM-dd') : '',
    status: data.status || 'draft',
    baseAirline: data.baseAirline || '',
    notes: data.notes || '',
  } : {
    seasonCode: '', seasonName: '', iataCode: 'S',
    year: new Date().getFullYear(), startDate: '', endDate: '',
    status: 'draft', baseAirline: '', notes: '',
  }, [data]);

  return (
    <Dialog 
      fullWidth 
      fullScreen={isMobile}
      maxWidth="sm" 
      open={open} 
      onClose={onClose} 
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <CalendarMonth fontSize="small" color="primary" />
          <Box>
            <Typography variant="subtitle1" fontWeight="bold" lineHeight={1.2}>
              {data ? 'Edit Season' : 'New Season'}
            </Typography>
            {data && (
              <Typography variant="caption" color="text.secondary">{data.seasonCode}</Typography>
            )}
          </Box>
        </Box>
      </DialogTitle>

      <Formik 
        initialValues={initialValues} 
        validationSchema={schema} 
        onSubmit={onSave} 
        enableReinitialize
      >
        {({ 
          values, 
          errors, 
          touched, 
          handleChange, 
          handleBlur, 
          handleSubmit, 
          isSubmitting, 
          setFieldValue 
        }) => (
          <form 
            onSubmit={handleSubmit} 
            style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              flex: 1, 
              overflow: 'hidden' 
            }}
          >
            <DialogContent dividers>
              <Box 
                display="grid" 
                gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }} 
                gap={2}
              >

                <TextField
                  variant="filled" 
                  fullWidth 
                  label="Season Code *" 
                  name="seasonCode"
                  value={values.seasonCode} 
                  onChange={handleChange} 
                  onBlur={handleBlur}
                  error={touched.seasonCode && Boolean(errors.seasonCode)}
                  helperText={touched.seasonCode && errors.seasonCode}
                  slotProps={{ htmlInput: { style: { textTransform: 'uppercase' } } }}
                />

                <TextField
                  variant="filled" 
                  fullWidth 
                  label="Year *" 
                  name="year" 
                  type="number"
                  value={values.year} 
                  onChange={handleChange} 
                  onBlur={handleBlur}
                  error={touched.year && Boolean(errors.year)}
                  helperText={touched.year && errors.year}
                />

                <TextField
                  variant="filled" 
                  fullWidth 
                  label="Season Name *" 
                  name="seasonName"
                  value={values.seasonName} 
                  onChange={handleChange} 
                  onBlur={handleBlur}
                  error={touched.seasonName && Boolean(errors.seasonName)}
                  helperText={touched.seasonName && errors.seasonName}
                  sx={{ gridColumn: 'span 2' }}
                />

                {/* IATA Type toggle */}
                <Box sx={{ gridColumn: 'span 2' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 0.75, display: 'block' }}>
                    Season Type *
                  </Typography>
                  <ToggleButtonGroup
                    value={values.iataCode}
                    exclusive
                    onChange={(_, v) => v && setFieldValue('iataCode', v)}
                    fullWidth
                  >
                    <ToggleButton value="S" sx={{ py: 1.5, gap: 1 }}>
                      <WbSunny
                        fontSize="small"
                        sx={{ color: values.iataCode === 'S' ? '#f57c00' : 'text.secondary' }}
                      />
                      <Typography variant="body2">Summer (S)</Typography>
                    </ToggleButton>
                    <ToggleButton value="W" sx={{ py: 1.5, gap: 1 }}>
                      <AcUnit
                        fontSize="small"
                        sx={{ color: values.iataCode === 'W' ? '#0288d1' : 'text.secondary' }}
                      />
                      <Typography variant="body2">Winter (W)</Typography>
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>

                <TextField
                  variant="filled" 
                  fullWidth 
                  label="Start Date *" 
                  name="startDate" 
                  type="date"
                  value={values.startDate} 
                  onChange={handleChange} 
                  onBlur={handleBlur}
                  slotProps={{ inputLabel: { shrink: true } }}
                  error={touched.startDate && Boolean(errors.startDate)}
                  helperText={touched.startDate && errors.startDate}
                />

                <TextField
                  variant="filled" 
                  fullWidth 
                  label="End Date *" 
                  name="endDate" 
                  type="date"
                  value={values.endDate} 
                  onChange={handleChange} 
                  onBlur={handleBlur}
                  slotProps={{ inputLabel: { shrink: true } }}
                  error={touched.endDate && Boolean(errors.endDate)}
                  helperText={touched.endDate && errors.endDate}
                />

                <TextField
                  variant="filled" 
                  fullWidth 
                  select 
                  label="Status" 
                  name="status"
                  value={values.status} 
                  onChange={handleChange}
                >
                  {STATUS_OPTIONS.map((s) => (
                    <MenuItem key={s} value={s}>
                      <Box display="flex" alignItems="center" gap={1.5}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: STATUS_DOT[s], flexShrink: 0 }} />
                        {s.charAt(0).toUpperCase() + s.slice(1)}
                      </Box>
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  variant="filled" 
                  fullWidth 
                  label="Base Airline" 
                  name="baseAirline"
                  value={values.baseAirline} 
                  onChange={handleChange}
                  slotProps={{ htmlInput: { style: { textTransform: 'uppercase' }, maxLength: 3 } }}
                />

                <TextField
                  variant="filled" 
                  fullWidth 
                  multiline 
                  rows={1} 
                  label="Notes" 
                  name="notes"
                  value={values.notes} 
                  onChange={handleChange}
                  sx={{ gridColumn: 'span 2' }}
                />

              </Box>
            </DialogContent>

            <DialogActions>
              <Button
                fullWidth={isMobile}
                variant="outlined"
                color="secondary"
                disabled={isSubmitting}
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                fullWidth={isMobile}
                type="submit"
                variant="outlined"
                disabled={isSubmitting}
                startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
              >
                {isSubmitting ? 'Saving…' : data ? 'Update' : 'Create'}
              </Button>
            </DialogActions>
          </form>
        )}
      </Formik>
    </Dialog>
  );
};

export default SeasonFormDialog;
